import { openDB } from 'idb'
import { updateFuncs } from '../store/plugins/DbCachePlugin'
import SocketRx from './SocketRx'
import TypeNames from './Typenames'
import { toI18nKey } from './Typenames'
import _ from 'lodash'
import { ActionTypes, store } from 'store'
import { i18n } from 'i18n'

// This file serves as a persistent caching layer between Vuex and the Http module

const dbName = 'cuestaCache'
const dbVersion = 2 // 1: initial

function registerTask(descriptionKey, promise, typename, count, args) {
  const localeArgs = _.extend({}, args || {}, {
    typename: i18n.tc(toI18nKey(typename || ''), count || 2)
  })
  const description = i18n.t(descriptionKey, localeArgs)
  store.dispatch(ActionTypes.REGISTER_TASK, { task: { description }, promise })
  return promise
}

// IMPORTANT: Do not change this function! Instead bump to next dbVersion and create another upgrade
// function with the changes
function setupSchemaVersion1(db) {
  // Db is uninitialized - set up object stores for the various entities - along with indexes as required

  db.createObjectStore(TypeNames.APP, { keyPath: 'id' })
  db.createObjectStore(TypeNames.APPTYPE, { keyPath: 'id' })
  db.createObjectStore(TypeNames.GROUP, { keyPath: 'id' })
  db.createObjectStore(TypeNames.MANUFACTURER, { keyPath: 'id' })

  const flowStore = db.createObjectStore(TypeNames.FLOW, { keyPath: 'id' })
  flowStore.createIndex('application', 'application', { unique: false })
  flowStore.createIndex('maintainer', 'maintainer', { unique: false })

  const fieldStore = db.createObjectStore(TypeNames.FIELD, { keyPath: 'id' })
  fieldStore.createIndex('application', 'application', { unique: false })

  db.createObjectStore(TypeNames.HUB, { keyPath: 'id' })
  db.createObjectStore(TypeNames.SERVICE, { keyPath: 'id' })
  db.createObjectStore(TypeNames.SUBJECT, { keyPath: 'id' })
  db.createObjectStore('suggestion', { keyPath: 'id' })
  db.createObjectStore(TypeNames.TABLE, { keyPath: 'id' })

  const userStore = db.createObjectStore(TypeNames.USER, { keyPath: 'id' })
  userStore.createIndex('name', 'name', { unique: false })

  db.createObjectStore(TypeNames.MANATEESETTINGS, { keyPath: 'id' })

  db.createObjectStore(TypeNames.RESTRICTIONS, { keyPath: 'id' })
}

function setupSchemaVersion2(db) {
  db.createObjectStore(TypeNames.ESTERO_ALERT, { keyPath: 'id' })
  db.createObjectStore(TypeNames.ESTERO_ALERT_LOG, { keyPath: 'id' })
}

async function initCuestaCache() {
  return await registerTask(
    'PREPARE_CACHE',
    openDB(dbName, dbVersion, {
      upgrade(db, oldVersion, newVersion, transaction) {
        console.log(`Cache db upgrading ${oldVersion} -> ${newVersion}`)
        if (oldVersion < 1) setupSchemaVersion1(db)
        if (oldVersion < 2) setupSchemaVersion2(db)
      },
      blocked() {
        console.debug('IndexedDb blocked callback', arguments)
      },
      blocking() {
        console.debug('IndexedDb blocking callback', arguments)
      },
      terminated() {
        console.debug('IndexedDb terminated callback', arguments)
      }
    })
  )
}

let initializePromise

// Make sure we only start async initialization once
async function initializeWithCache(dataSource) {
  if (!initializePromise) {
    initializePromise = initialize(dataSource)
  }
  return await initializePromise
}

// Pass in a datasource for the cache to fetch data from
// Note: it needs to expose the 'getAllChanged' method
async function initialize(dataSource) {
  console.log('initCuestaCache')
  const db = await initCuestaCache()

  // Each type needs only be synced the first time it is accessed
  const syncedTypes = {}

  async function syncTypeAsync(typename, allDbItems): Promise {
    if (syncedTypes[typename]) return
    syncedTypes[typename] = true
    try {
      if (!allDbItems) console.time(typename + ': retrieve-db')
      const allItems =
        allDbItems || (await registerTask('READ_MANY_FROM_CACHE', db.getAll(typename), typename))
      if (!allDbItems) console.timeEnd(typename + ': retrieve-db')

      console.debug(`db '${typename}' objects before initial sync`, allItems.length)
      const itemKnownVersions = _.zipObject(allItems.map(x => x.id), allItems.map(x => x.version))

      console.time(typename + ': retrieve-server')
      const changedItems = await registerTask(
        'READ_CHANGED_FROM_SERVER',
        dataSource.getAllChanged(typename, itemKnownVersions),
        typename
      )
      console.timeEnd(typename + ': retrieve-server')
      const changesDetected = changedItems.results.length || changedItems.deleted.length
      // Update the db with the newly received data in a transaction
      if (changesDetected) {
        console.debug(
          `Changed '${typename}' from server in db sync (update/delete): (${changedItems.results.length}/${changedItems.deleted.length})`
        )
        console.time(typename + ': update-db')
        const tx = db.transaction(typename, 'readwrite')
        const itemPromises = changedItems.results.map(item => tx.objectStore(typename).put(item))
        const itemDeletePromises = changedItems.deleted.map(deletedId =>
          tx.objectStore(typename).delete(deletedId)
        )
        await registerTask(
          'APPLY_DELTAS',
          Promise.all([...itemPromises, ...itemDeletePromises, tx.done]),
          typename
        )
        console.timeEnd(typename + ': update-db')

        // Notify UI via Vuex that entities have changed/gone away
        console.debug(`IndexedDb wishes to notify for type ${typename}`, updateFuncs)
        changedItems.results.forEach(
          item => updateFuncs[typename] && updateFuncs[typename].update(item)
        )
        changedItems.deleted.forEach(
          id => updateFuncs[typename] && updateFuncs[typename].delete(id)
        )
      }
    } catch (e) {
      console.warn(`DB sync for '${typename}' failed `, e)
      syncedTypes[typename] = false
      throw e
    }
  }

  //
  // Data query functions
  //

  // NOTE: These operate on an 'eventual consistency' basis. They may produce stale data initially,
  // but the asynchronous sync with kwanza will provide fresh data shortly thereafter

  async function getById(typename: string, id: string): Promise<Object> {
    syncTypeAsync(typename)
    return registerTask('GET_BY_ID', db.get(typename, id), typename, 1)
  }

  async function getByName(typename: string, name: string): Promise<Array<Object>> {
    syncTypeAsync(typename)
    return await registerTask(
      'GET_BY_NAME',
      db.getAllFromIndex(typename, 'name', name),
      typename,
      1,
      { name }
    )
  }

  const pendingGetAllByType = {}
  async function getAll(typename: string): Promise<Array<Object>> {
    // Optimization: Parallel invocations for the same type share the db promise
    const existingPromise = pendingGetAllByType[typename]
    let allItems
    if (existingPromise) {
      console.debug('Re-using getAll promise', typename)
      allItems = await existingPromise
    } else {
      console.debug('Creating getAll promise', typename)
      console.time(typename + ': retrieve-getAll')
      try {
        allItems = await registerTask(
          'READ_MANY_FROM_CACHE',
          (pendingGetAllByType[typename] = db.getAll(typename)),
          typename
        )
        syncTypeAsync(typename, allItems)
      } catch (e) {
        console.warn('Error getting all', typename)
        throw e
      } finally {
        console.timeEnd(typename + ': retrieve-getAll')
        pendingGetAllByType[typename] = null
      }
    }
    return allItems
  }

  async function getForApp(typename: string, appId: string): Promise<Array<Object>> {
    syncTypeAsync(typename)
    // Note: We assume presence of 'application' index for the given typename
    return registerTask('GET_FOR_APP', db.getAllFromIndex(typename, 'application', appId), typename)
  }

  // Used for looking up flows by maintainer
  async function getForMaintainer(typename: string, userId: string): Promise<Array<Object>> {
    syncTypeAsync(typename)
    // Note: We assume presence of 'maintainer' index for the given typename
    return registerTask(
      'GET_FOR_MAINTAINER',
      db.getAllFromIndex(typename, 'maintainer', userId),
      typename
    )
  }

  async function clearAllStores() {
    for (const storeName of db.objectStoreNames) {
      await db.clear(storeName)
    }
  }

  //
  // Data mutator intercept functions
  //

  async function updateItem(typename: string, id: string, item: Object): Promise<PostResponse> {
    return registerTask(
      'SAVE_TYPE',
      dataSource.updateItem(typename, id, item).then(async response => {
        // NOTE: db.put silently adds if the id was not already present
        await db.put(typename, { ...item, version: response.version })
        return response
      }),
      typename,
      1
    )
  }

  async function createItem(typename: string, item: Object): Promise<PostResponse> {
    return registerTask(
      'CREATE_TYPE',
      dataSource.createItem(typename, item).then(async response => {
        try {
          await db.add(typename, { ...item, id: response.id, version: response.version })
          return response
        } catch (e) {
          /* Ignored since it is generally caused by this handler being in a race with the ws 'created' event */
          return response
        }
      }),
      typename,
      1
    )
  }

  async function deleteItem(typename: string, id: string): Promise<any> {
    return registerTask(
      'DELETE_TYPE',
      dataSource.deleteItem(typename, id).then(async response => {
        await db.delete(typename, id)
        return response
      }),
      typename,
      1
    )
  }

  //
  // Keep db up-to-date after init with socket event stream
  //

  const mapData = data => {
    return { id: data.id, name: data.name, version: data.version, ..._.cloneDeep(data.descriptive) }
  }

  // Note: each SocketRx.open call recycles the socket if it is already open or else causes it to open
  const rxStreams = Object.values(TypeNames).map(typename => ({
    typename,
    streams: SocketRx.open(typename, '')
  }))

  rxStreams.forEach(({ typename, streams }) => {
    streams.onCreate.subscribe(async data => {
      try {
        // Note: 'add' sometimes fails here due to race with dataSource.post completion which also adds the item
        await db.add(typename, mapData(data))
      } catch (e) {
        /* Ignore */
      }
    })
    streams.onUpdate.subscribe(async data => {
      try {
        await db.put(typename, mapData(data))
      } catch (e) {
        /* Ignore */
      }
    })
    streams.onDelete.subscribe(async data => {
      try {
        await db.delete(typename, data.id)
      } catch (e) {
        /* Ignore */
      }
    })
  })
  return {
    getById,
    getByName,
    getAll,
    getForApp,
    getForMaintainer,
    updateItem,
    createItem,
    deleteItem,
    clearAllStores,
    forTesting: {
      db
    }
  }
}

export default {
  initialize: initializeWithCache,
  getExisting: () => initializePromise,
  forTesting: {
    initializeNoCache: initialize
  }
}
